<template>
    <div>
        <component :is="component" v-if="localized" v-bind="$props" />
        <v-radio
            v-else
            :key="paymentPlan.planId"
            class="d-flex radio-control custom-plan"
            active-class="active"
            color="white"
            on-icon="$checkCircle"
            :value="paymentPlan"
        >
            <template #label>
                <div class="radio-label align-content-center">
                    <div class="provider-logo-container">
                        <ResponsiveImage :image-url="providerLogo" height="76" />
                    </div>

                    <div class="payment-plan-content">
                        <div class="description">
                            <div class="title mt-1">
                                {{ $t(`plan.type.${paymentPlan.typeId}.review-header`) }}
                            </div>
                            <span class="label">
                                {{ paymentPlan.durationMonths }} months @ {{ paymentPlan.apr.toFixed(2) }}% APR
                            </span>
                            <div :class="{ 'text-error': !fullyCovered }">
                                <span>
                                    <b :class="{ 'label text-error': !fullyCovered, label: fullyCovered }">
                                        Financed Amount:
                                    </b>
                                    {{
                                        getFinancedAmount(
                                            fullyCovered,
                                            isUnderMinimum,
                                            financedAmountCents,
                                            paymentPlan
                                        ) | currencyWhole
                                    }}
                                </span>
                            </div>

                            <div v-if="!fullyCovered">
                                <span class="text-error"> *Requested financed amount not fully covered </span>
                            </div>
                            <span>
                                <b class="label">Offer Exp:</b>
                                {{ paymentPlan.expireDateTime | date }}
                                ({{ paymentPlan.expireDateTime | days }} days)
                            </span>
                        </div>
                    </div>
                    <div class="pp-monthly-payment-container">
                        <div class="pp-monthly-payment">{{ monthlyPayment }}/mo</div>
                    </div>
                </div>
            </template>
        </v-radio>
    </div>
</template>

<script>
    import LocalizedComponentMixin from "@/components/localization/LocalizedComponentMixin"
    import { applicationState } from "@/state"
    import { formatCurrency } from "@/util/currency"
    import { getFinancedAmount } from "@/util/plan"
    import PaymentPlanType from "@/model/apply/PaymentPlanType"
    import Providers from "@/model/provider/Providers"
    import DynamicProviderKey from "@/model/provider/DynamicProviderKey"

    export default {
        name: "PaymentPlanOption",
        mixins: [LocalizedComponentMixin],
        props: {
            financedAmountCents: {
                type: Number,
            },
            paymentPlan: {
                type: Object,
                required: true,
            },
            isUnderMinimum: {
                type: Boolean,
                required: true,
            },
            fullyCovered: {
                type: Boolean,
                required: true,
            },
            providerKey: {
                type: String,
                required: false,
            },
            showMinimumAmount: {
                type: Boolean,
            },
        },

        data() {
            const treatmentTotal = applicationState.application.purchaseAmountCents
            const creditLimitRequested = this.getCreditLimitRequested()
            const remainingPatientObligation = this.getRemainingPatientObligation()
            return {
                componentType: "paymentPlanOption",
                getFinancedAmount,
                formattedTreatmentTotal: formatCurrency(treatmentTotal, "cents", true),
                formattedCreditLimitRequested: formatCurrency(creditLimitRequested, "cents", true),
                formattedRemainingPatientObligation: formatCurrency(remainingPatientObligation, "cents", true),
                tooltipOn: null,
            }
        },

        computed: {
            isFixedLoanSubLender() {
                return this.paymentPlan.typeId && PaymentPlanType.SUB_LENDER_FIXED_LOAN === this.paymentPlan.typeId
            },
            monthlyPayment() {
                return formatCurrency(this.paymentPlan.monthlyAmountCents, "cents", false)
            },
            providerLogo() {
                if (this.paymentPlan.typeId === PaymentPlanType.SUB_LENDER_FIXED_LOAN) {
                    return Providers[DynamicProviderKey.extractProviderKey(this.providerKey)]?.subLender?.logo
                }
                return Providers[DynamicProviderKey.extractProviderKey(this.providerKey)]?.logo
            },
        },

        methods: {
            onTooltipActivatorClick(e, on) {
                // e.preventDefault()
                // e.stopPropagation()
                this.tooltipOn = on
                on.click(e)
            },

            onTooltipBlur(e) {
                if (this.tooltipOn) {
                    this.tooltipOn.blur(e)
                }
            },
            getInsuranceBenefitsCents() {
                return applicationState.application.insurance?.insuranceBenefitsCents || 0
            },
            getCreditLimitRequested() {
                return Math.min(
                    200000,
                    Math.floor(
                        (applicationState.application.purchaseAmountCents + this.getInsuranceBenefitsCents()) / 2
                    )
                )
            },
            getRemainingPatientObligation() {
                return Math.max(0, applicationState.application.purchaseAmountCents - this.getCreditLimitRequested())
            },
        },
    }
</script>
